import { useSpace } from '@ably/spaces/react';
import {
  faArrowsRotate,
  faInfoCircle,
  faLink,
  faPlus,
  faRefresh,
  faUpload,
  faRecycle,
  faTable,
  faToilet,
  faChartLine,
  faEllipsisVertical,
  faSpinner,
  faTimes,
  faTrash,
  faChevronRight,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Message } from 'ably';
import {
  ColDef,
  ColumnState,
  GridOptions,
  RowClassParams,
  RowDataTransaction,
  ClientSideRowModelModule,
  ModuleRegistry,
  ValidationModule,
  RowStyleModule,
  RowSelectionModule,
  TextFilterModule,
  ColumnAutoSizeModule,
  RowApiModule,
  NumberFilterModule,
  TooltipModule,
  CellStyleModule,
  ColumnApiModule,
  DateFilterModule,
  themeBalham,
  ClientSideRowModelApiModule,
  ScrollApiModule,
  colorSchemeDark
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { billingReportChannelName } from '@/channels/utils/channelNames';
import AppContext from '@/contexts/appContext';
import { useChannelData } from '@/hooks/useChannel';
import useDugoutBillingRowSelection from '@/hooks/useDugoutBillingRowSelection';
import AvatarStackForSpace from '@/ui/components/AvatarStackForSpace';
import Button from '@/ui/components/Button';
import Tooltip from '@/ui/components/Tooltip';
import ajax from '@/utils/ajax';
import { hasPermission } from '@/utils/userPermission';

import { createBillingRowColumnDefs } from './columnDefs';
import AssociateServiceLines from './components/AssociateServiceLines';
import { BillingReportErrorSummary } from './components/BillingReportErrorSummary';
import BillingReportFilters from './components/BillingReportFilters';
import CopyUrlToClipboard from './components/CopyUrlToClipboard';
import FilterExplanation from './components/FilterExplanation';
import IgnoreErrorServiceLines from './components/IgnoreErrorServiceLines';
import ManualHoldServiceLines from './components/ManualHoldServiceLines';
import ReportOverview from './components/ReportOverview';
import RowEditor from './components/RowEditor';
import {
  MANUALLY_HOLD_PERMISSIONS,
  IGNORE_ERROR_PERMISSIONS,
  REFRESH_ROW_PERMISSIONS,
  DELETE_ROW_PERMISSIONS,
  EDIT_ROW_PERMISSIONS,
  ASSOCIATE_CLAIM_PERMISSIONS,
  SUBMIT_BILLING_REPORT_PERMISSIONS
} from './constants';
import { AssociateBillingRowsProvider } from './contexts/AssociateBillingRowsContext';
import { BillingFlagReasonProvider } from './contexts/BillingFlagReasonContext';
import { BillingReportProvider } from './contexts/BillingReportProvider';
import BillingItemFlagRepository from './repositories/BillingItemFlagRepository';
import BillingReportResultRepository from './repositories/BillingReportResultRepository';
import {
  BillingReportResult,
  BillingReportRow,
  Props,
  UiMessage,
  BillingReportEvent,
  isRefreshStartedEvent,
  isRefreshCompleteEvent,
  isSubmitStartedEvent,
  isSubmitCompleteEvent,
  isRefreshFailedEvent,
  isSubmitFailedEvent
} from './types';
import {
  BillingItemFlagType,
  BulkBillingItemFlagPayload,
  BulkIgnoreErrorsFlag
} from './types/billing_item_flag';
import { Cms1500 } from './types/cms_1500';
import { LinkedServiceLine } from './types/linked_service';

interface BillingReportActionResponse {
  message: string;
  billing_report_result_id?: string;
}

interface FlushLookupsResponse {
  message: string;
}

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  ValidationModule,
  RowStyleModule,
  RowSelectionModule,
  TextFilterModule,
  ColumnAutoSizeModule,
  RowApiModule,
  NumberFilterModule,
  TooltipModule,
  CellStyleModule,
  ColumnApiModule,
  DateFilterModule,
  ScrollApiModule
]);

// Max rows is now bound by kb size of ids. Max message is 64kb, so uuidv4 16 bytes
const MAX_ROWS_TO_REFRESH = 2000;

const BillingReportResultApp = ({
  healthie_url,
  billing_report_id,
  billing_report_result_id,
  user,
  permissions
}: Props): JSX.Element => {
  const gridRef = useRef<AgGridReact<BillingReportRow>>(null); // Add type for useRef
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    initialData: false,
    deleting: false,
    holding: false,
    clearingHold: false,
    ignoringErrors: false,
    associating: false,
    regenerating: false,
    submittingAll: false,
    flushingLookups: false,
    loadingProgress: { rows: 0, totalRows: 0 }
  });
  const { host, resolvedTheme } = useContext(AppContext);

  // Subscribe to Ably channel for real-time billing report updates
  useChannelData({
    channelId: billingReportChannelName(billing_report_id, billing_report_result_id),
    channelName: `billing-report-${billing_report_id}-${billing_report_result_id}`,
    messageCallback: async (message: Message) => {
      const data = JSON.parse(message.data as string) as BillingReportEvent;
      if (isRefreshCompleteEvent(data) || isSubmitCompleteEvent(data)) {
        // Fetch the updated rows
        const idsToFetch = [...(data.add_ids || []), ...(data.update_ids || [])];
        const fetchedRows = await fetchRowsByIds(idsToFetch);

        // Separate add and update rows using Sets and a single pass
        const addIdSet = new Set(data.add_ids || []);
        const updateIdSet = new Set(data.update_ids || []);
        const addRows: BillingReportRow[] = [];
        const updateRows: BillingReportRow[] = [];
        fetchedRows.forEach((row) => {
          if (addIdSet.has(row.billing_item_id)) {
            addRows.push(row);
          } else if (updateIdSet.has(row.billing_item_id)) {
            updateRows.push(row);
          }
        });

        // https://www.ag-grid.com/react-data-grid/data-update-transactions/#identifying-rows-for-update-and-remove
        // deleting a row, only the ID is needed, other attributes (name, age) don't serve any purpose
        const removeRows = (data.remove_ids || []).map((el) => ({ billing_item_id: el }));

        partialUpdateAgGrid({
          add: addRows,
          update: updateRows,
          remove: removeRows
        });
      }

      if (isSubmitStartedEvent(data)) {
        data.ids.forEach((id: string) => {
          const row = gridRows.find((r) => r.billing_item_id === id);
          if (row) {
            row.isSubmitting = true;
          }
        });
      }

      if (isRefreshStartedEvent(data)) {
        data.ids.forEach((id: string) => {
          const row = gridRows.find((r) => r.billing_item_id === id);
          if (row) {
            row.isRefreshing = true;
          }
        });
      }

      if (isRefreshFailedEvent(data) || isSubmitFailedEvent(data)) {
        data.ids.forEach((id: string) => {
          const row = gridRows.find((r) => r.billing_item_id === id);
          if (row) {
            row.isSubmitting = false;
            row.isRefreshing = false;
          }
        });
      }
    }
  });

  const [gridRows, setGridRows] = useState<BillingReportRow[]>([]);
  const [showAssociateClaimPanel, setShowAssociateClaimPanel] = useState(false);
  const [editingSelectedRows, setEditingSelectedRows] = useState(false);

  const [messages, setMessages] = useState<UiMessage[]>([]);

  const setErrorMessage = useCallback((message: string) => {
    setMessages([{ message, type: 'error' }]);
  }, []);

  const resetMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const reportRef = useRef<HTMLDivElement>(null);
  const gridStyle = useMemo(() => ({ height: '600px', width: '100%' }), []);
  const gridOptions: GridOptions<BillingReportRow> = {
    tooltipShowDelay: 250,
    tooltipHideDelay: 3000,
    maintainColumnOrder: true,
    rowClassRules: {
      'pending-association-highlight': (params: RowClassParams) => {
        return !!params.node.data?.pending_cms_1500_healthie_id;
      }
    }
  };
  const [hasSelection, setHasSelection] = useState(false);
  const [billingReportResult, setBillingReportResult] = useState<BillingReportResult | undefined>();

  const [selectedRows, setSelectedRows] = useState<BillingReportRow[]>([]);
  const [showManualHoldInput, setShowManualHoldInput] = useState(false);
  const [showIgnoreErrorInput, setShowIgnoreErrorInput] = useState(false);
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const [rowCount, setRowCount] = useState(gridRows.length);
  // Bumped on every filter change so the current-filter chips (which read the
  // grid's filter model directly) re-render even when the row count is
  // unchanged -- e.g. switching between two quick filters that both match no
  // rows.
  const [, setFilterVersion] = useState(0);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  const { space } = useSpace();
  const { showSelectedRowInDugout } = useDugoutBillingRowSelection();

  useEffect(() => {
    if (space && user) {
      space.enter({
        name: user.first_name + ' ' + user.last_name,
        email: user.email,
        avatar_url: user.avatar_url
      });
    }
    // Leave space on unmount
    return () => {
      space?.leave();
    };
  }, [space, user]);

  // Optional metadata fields provided by server in some deployments. Guarded as they may be missing.
  const googleSheetId = billingReportResult
    ? ((billingReportResult as unknown as Record<string, unknown>)['google_sheet_id'] as
        string | undefined)
    : undefined;

  const datadogUrl = billingReportResult
    ? ((billingReportResult as unknown as Record<string, unknown>)['datadog_url'] as
        string | undefined)
    : undefined;

  const billingLookupSheetId = billingReportResult
    ? ((billingReportResult as unknown as Record<string, unknown>)['billing_lookup_sheet_id'] as
        string | undefined)
    : undefined;

  const reportEndAt = useMemo(() => {
    if (billingReportResult) {
      return new Date(billingReportResult?.billing_report?.range_end);
    }
    return null;
  }, [billingReportResult]);

  const currentUser = user;
  const apiToken: string = currentUser.api_key;
  const reportRepository = BillingReportResultRepository(
    host,
    parseInt(billing_report_result_id, 10)
  );
  const billingItemFlagRepository = BillingItemFlagRepository(host);

  const fetchRowsByIds = useCallback(
    async (billingItemIds: string[]): Promise<BillingReportRow[]> => {
      if (billingItemIds.length === 0) return [];

      try {
        const response = await ajax.get({
          url: `${host}/api/billing/billing_report_results/${billing_report_result_id}/rows/?billing_item_ids=${billingItemIds.join(',')}`
        });

        if (response.status === 200) {
          return response.data.results || [];
        } else {
          console.error('Failed to fetch rows by IDs:', response.status);
          return [];
        }
      } catch (error) {
        console.error('Error fetching rows by IDs:', error);
        return [];
      }
    },
    [billing_report_result_id, host]
  );

  const handleRegenerateBillingReport = useCallback(async () => {
    if (!billingReportResult?.billing_report?.id) return;

    const confirmed = window.confirm(
      'Are you sure? This will add back items that have previously been removed from the report and remove any edits'
    );
    if (!confirmed) return;

    setLoading((prev) => ({ ...prev, regenerating: true }));
    resetMessages();
    try {
      const response = await ajax.post({
        url: `${host}/api/billing/billing_report_results/${billingReportResult.billing_report.id}/regenerate/`,
        data: {}
      });

      if (response.status === 202) {
        const data = response.data as BillingReportActionResponse;
        setMessages([
          {
            message: data.message || 'Billing report processing has started',
            type: 'success'
          }
        ]);
        // Optionally redirect to the new result
        if (data.billing_report_result_id) {
          setTimeout(() => {
            window.location.href = `/billing/reports/${billingReportResult.billing_report.id}/results/${data.billing_report_result_id}/`;
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error regenerating billing report:', error);
      setErrorMessage('Failed to regenerate billing report');
    } finally {
      setLoading((prev) => ({ ...prev, regenerating: false }));
    }
  }, [billingReportResult, host, resetMessages, setErrorMessage]);

  const handleSubmitAllBillingReport = useCallback(async () => {
    if (!billingReportResult?.billing_report?.id) return;

    const confirmed = window.confirm('Are you sure?');
    if (!confirmed) return;

    setLoading((prev) => ({ ...prev, submittingAll: true }));
    resetMessages();
    try {
      const response = await ajax.post({
        url: `${host}/api/billing/billing_report_results/${billingReportResult.billing_report.id}/submit/`,
        data: {}
      });

      if (response.status === 202) {
        const data = response.data as BillingReportActionResponse;
        setMessages([
          {
            message: data.message || 'Billing report submission has started',
            type: 'success'
          }
        ]);
      }
    } catch (error) {
      console.error('Error submitting billing report:', error);
      setErrorMessage('Failed to submit billing report');
    } finally {
      setLoading((prev) => ({ ...prev, submittingAll: false }));
    }
  }, [billingReportResult, host, resetMessages, setErrorMessage]);

  const handleFlushLookups = useCallback(async () => {
    setLoading((prev) => ({ ...prev, flushingLookups: true }));
    resetMessages();
    try {
      const response = await ajax.post({
        url: `${host}/api/billing/billing_report_results/flush_lookups/`,
        data: {}
      });

      if (response.status === 200) {
        const data = response.data as FlushLookupsResponse;
        setMessages([
          {
            message: data.message || 'All billing lookups have been flushed.',
            type: 'success'
          }
        ]);
      }
    } catch (error) {
      console.error('Error flushing lookups:', error);
      setErrorMessage('Failed to flush lookups');
    } finally {
      setLoading((prev) => ({ ...prev, flushingLookups: false }));
    }
  }, [host, resetMessages, setErrorMessage]);

  const loadGridRows = useCallback(async () => {
    if (gridRows.length === 0 && !loading.initialData) {
      setLoading({ ...loading, initialData: true });

      try {
        // First, load the billing report result metadata
        const reportResponse = await ajax.get({
          url: `${host}/api/billing/billing_report_results/${billing_report_result_id}/`
        });

        if (reportResponse.status === 200) {
          setBillingReportResult(reportResponse.data);
        } else {
          setErrorMessage('Failed to fetch report metadata');
          return;
        }

        // Now sequentially load paginated row data
        const allRows: BillingReportRow[] = [];
        let page = 1;
        let hasMorePages = true;

        while (hasMorePages) {
          try {
            // Update loading progress
            setLoading((prev) => ({
              ...prev,
              loadingProgress: {
                totalRows: reportResponse.data.row_count,
                rows: allRows.length
              }
            }));

            const rowsResponse = await ajax.get({
              url: `${host}/api/billing/billing_report_results/${billing_report_result_id}/rows/?page=${page}`
            });

            if (rowsResponse.status === 200) {
              const pageData = rowsResponse.data;

              // Add the current page's rows to our collection
              if (pageData.results && pageData.results.length > 0) {
                allRows.push(...pageData.results);

                // Update the grid with current data for progressive loading
                updateAgGrid([...allRows]);

                // Check if there are more pages
                hasMorePages = !!pageData.links?.next;
                page++;
              } else {
                hasMorePages = false;
              }
            } else {
              console.error(`Failed to fetch page ${page}:`, rowsResponse.status);
              hasMorePages = false;
            }
          } catch (pageError) {
            console.error(`Error loading page ${page}:`, pageError);
            hasMorePages = false;
          }
        }

        // Final update with all rows loaded
        updateAgGrid(allRows);
        resizeGridColumnsToFitData();
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorMessage = `Failed to fetch report data ${
            error.response?.status
          } ${JSON.stringify(error.response?.data)}`;

          setErrorMessage(errorMessage);
        } else {
          setErrorMessage(JSON.stringify(error));
          console.error(error);
        }
      } finally {
        resizeGridColumnsToFitData();
        setLoading((prev) => ({
          ...prev,
          initialData: false,
          loadingProgress: { rows: 0, totalRows: 0 }
        }));
      }
    }
  }, [billing_report_result_id, gridRows.length, host, loading, setErrorMessage, updateAgGrid]);

  const resizeGridColumnsToFitData = () => {
    if (!gridRef.current) return;

    const allColumnIds: string[] = [];
    (gridRef.current?.api?.getColumns() || []).forEach((column) => {
      if (column.getColDef().suppressAutoSize) return;

      allColumnIds.push(column.getId());
    });
    gridRef.current?.api?.autoSizeColumns(allColumnIds, true);
  };

  /**
   *
   * Colunm definitions for the billing report grid.
   *
   */
  const colDefs: ColDef[] = useMemo(() => createBillingRowColumnDefs(host), [host]);

  /**
   * Restores the filter state of the grid from the URL parameters.
   * If there are filter parameters in the URL, it parses them and sets them as the filter model for the grid.
   * This function is called when the grid is ready.
   */
  const restoreFilterStateFromUrl = useCallback(() => {
    if (!gridRef.current) return;
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const filterModel = JSON.parse(params.get('filters') || '{}');
    const orderedColumns = (params.get('columns') || '').split(',');

    // Get the current column state
    const currentColumnState = gridRef.current.api.getColumnState();

    function getColByInitalIndex(index: number): string {
      return colDefs[index] ? colDefs[index].field || '' : '';
    }
    // sort currentColumnState by the order of the columns data passed in via columnsUrl
    const columnModel = orderedColumns
      .map((col: string) => {
        const components = col.split(':');
        const colId = getColByInitalIndex(parseInt(components[0]));
        const sort = components[1];
        const state = currentColumnState.find((column) => column.colId === colId);
        if (state) {
          if (sort) {
            state.sort = sort as 'asc' | 'desc';
          }
          return state;
        }
      })
      .filter((col): col is ColumnState => col !== undefined);

    // iterate over the url params and translate them to filter model
    gridRef.current.api.setFilterModel(filterModel);

    // iterate over the url params and translate them to column model
    gridRef.current.api.applyColumnState({
      state: columnModel,
      applyOrder: true
    });
  }, [colDefs]);

  /**
   * Resizes all columns in the grid to fit the available space.
   * If a column has suppressAutoSize set to true, it will not be resized.
   */
  const onGridReady = useCallback(() => {
    if (!gridRef.current) return;

    setTimeout(() => {
      loadGridRows();
      restoreFilterStateFromUrl();
    }, 0);

    console.log('onGridReady');
  }, [loadGridRows, restoreFilterStateFromUrl]);

  const onRowSelected = useCallback(() => {
    if (!gridRef.current || !gridRef.current.api) return;

    const rows = gridRef.current.api.getSelectedRows();
    setHasSelection(rows.length > 0);
    setSelectedRows(rows);
    showSelectedRowInDugout(rows);
  }, [showSelectedRowInDugout]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function updateAgGrid(rows: BillingReportRow[]) {
    if (!gridRef.current) return;

    gridRef.current?.api?.setGridOption('rowData', rows);
    setGridRows(rows);
    setRowCount(gridRef.current?.api?.getDisplayedRowCount() || 0);
    onRowSelected();
  }

  const partialUpdateAgGrid = useCallback(
    (transaction: RowDataTransaction) => {
      if (!gridRef.current) return;

      gridRef.current.api.applyTransaction(transaction);

      const rowData: BillingReportRow[] = [];
      gridRef.current.api.forEachNode((node) => {
        if (node.data) {
          rowData.push(node.data);
        }
      });

      // Only reset isRefreshing and isSubmitting flags for rows that were part of the transaction
      if (transaction.update) {
        const updatedIds = new Set(transaction.update.map((row) => row.billing_item_id));
        rowData.forEach((row) => {
          if (updatedIds.has(row.billing_item_id)) {
            row.isRefreshing = false;
            row.isSubmitting = false;
          }
        });
      }

      setGridRows(rowData);
      setRowCount(gridRef.current.api.getDisplayedRowCount());
      onRowSelected();
    },
    [gridRef, onRowSelected]
  );

  const markSelectedRowsAsIgnoreError = async (flag: BulkIgnoreErrorsFlag) => {
    const { reason, note, alerts, rows, type } = flag;
    if (!rows) return;

    setLoading({ ...loading, ignoringErrors: true });

    // Check if any of the rows have cms_1500_healthie_id
    // if so halt and show error
    const hasCms1500 = rows.some((row) => !!row.cms_1500_healthie_id);

    if (hasCms1500) {
      setErrorMessage('Cannot ignore errors on row with a CMS 1500 ID');
      setLoading({ ...loading, ignoringErrors: false });
      return;
    }
    const payload: BulkBillingItemFlagPayload = {
      type,
      reason,
      note,
      alerts,
      billingItemIds: rows.map((row: BillingReportRow) => row.billing_item_id)
    };
    try {
      await billingItemFlagRepository.addFlag(payload);
      resetMessages();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = `Failed to ignore errors for service line ${error.response?.data?.error}`;

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(JSON.stringify(error));
        console.error(error);
      }
    } finally {
      setLoading({ ...loading, ignoringErrors: false });
    }
  };

  const markSelectedRowsOnHold = async (
    reason: string,
    note: string | null,
    rows: BillingReportRow[]
  ) => {
    if (!rows) return;

    setLoading({ ...loading, holding: true });

    // Check if any of the rows have cms_1500_healthie_id
    // if so halt and show error
    const hasCms1500 = rows.some((row) => !!row.cms_1500_healthie_id);

    if (hasCms1500) {
      setErrorMessage('Cannot hold a row with a CMS 1500 ID');
      setLoading({ ...loading, holding: false });
      return;
    }

    const payload: BulkBillingItemFlagPayload = {
      type: BillingItemFlagType.MANUALLY_HELD,
      reason,
      note,
      billingItemIds: rows.map((row: BillingReportRow) => row.billing_item_id)
    };
    try {
      await billingItemFlagRepository.addFlag(payload);
      rows.forEach((row) => {
        row.manually_held_at = new Date().toISOString();
      });
      resetMessages();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = `Failed to ignore errors for service line ${error.response?.data?.error}`;

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(JSON.stringify(error));
        console.error(error);
      }
    } finally {
      setLoading({ ...loading, holding: false });
    }
  };

  const handleManualHold = async (reason: string, note: string, rows: BillingReportRow[]) => {
    await markSelectedRowsOnHold(reason, note, rows);
    setShowManualHoldInput(false);
    await refreshSelectedRows();
  };

  const handleIgnoreError = async (flag: BulkIgnoreErrorsFlag) => {
    await markSelectedRowsAsIgnoreError(flag);
    setShowIgnoreErrorInput(false);
    await refreshSelectedRows();
  };

  const handleClearFlag = async (type: string, onlyForUser: boolean, rows: BillingReportRow[]) => {
    const fieldToClear =
      type === BillingItemFlagType.IGNORE_ERROR ? 'ignore_error_at' : 'manually_held_at';

    if (type === BillingItemFlagType.MANUALLY_HELD) {
      setLoading({ ...loading, clearingHold: true });
    }

    await billingItemFlagRepository.clearFlags({
      type,
      onlyForUser,
      billingItemIds: rows.map((row) => row.billing_item_id)
    });

    // Optimistically clear the flag on the client side
    rows.forEach((row) => {
      row[fieldToClear] = '';
    });

    if (type === BillingItemFlagType.MANUALLY_HELD) {
      setLoading({ ...loading, clearingHold: false });
    }

    refreshSelectedRows();
  };

  const checkRowsAreAllOnHold = () => {
    if (!gridRef.current || !gridRef.current.api) return false;

    const rows = gridRef.current.api.getSelectedRows();
    const allOnHold = rows.every((row) => row.manually_held_at);

    return allOnHold;
  };

  const removeSelectedRows = async () => {
    if (!gridRef.current) return;

    setLoading({ ...loading, deleting: true });

    const nodes = gridRef.current.api.getSelectedNodes();
    const rows: BillingReportRow[] = nodes
      .map((row) => row.data)
      .filter((row): row is BillingReportRow => !!row);

    // Check if any of the rows have cms_1500_healthie_id
    // if so halt and show error
    const hasCms1500 = rows.some((row) => !!row.cms_1500_healthie_id);

    if (hasCms1500) {
      setErrorMessage('Cannot delete rows with CMS 1500 ID');
      setLoading({ ...loading, deleting: false });
      return;
    }

    const data = nodes.map((row) => ({
      index: row.rowIndex,
      action: 'remove',
      row: row.data
    }));

    try {
      await ajax.post({
        url: `${host}/api/billing/billing_report_results/${billing_report_result_id}/update_rows/`,
        data: { data }
      });

      resetMessages();
      gridRef.current.api.applyTransaction({
        remove: rows
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = `Failed to remove service line ${error.response?.data?.error}`;

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(JSON.stringify(error));
        console.error(error);
      }
    } finally {
      setLoading({ ...loading, deleting: false });
    }
  };

  const refreshSelectedRows = async () => {
    if (!gridRef.current || !gridRef.current.api) return;

    const rows = gridRef.current.api.getSelectedRows();

    if (rows.length >= MAX_ROWS_TO_REFRESH) {
      setMessages([
        {
          message: (
            <>
              <b>Over {MAX_ROWS_TO_REFRESH} items effected</b> Row level refresh is disabled
            </>
          ),
          type: 'info'
        }
      ]);
      return;
    }

    // Set refreshing flag on selected rows
    rows.forEach((row) => {
      row.isRefreshing = true;
    });
    updateAgGrid([...gridRows]);

    const billingItemIds = rows.map((row) => row.billing_item_id);
    await refreshRows(billingItemIds, true);
  };

  const refreshRows = async (billingItemIds: string[], async = true) => {
    try {
      const rows = await reportRepository.refresh_rows(billingItemIds, async);
      if (rows) {
        console.log('Refreshed rows:', rows);
      } else {
        setErrorMessage('Failed to refresh service lines for unknown reason, Check logs');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = `Failed to refresh service line
        ${error.response?.status}
        ${error.response?.data?.error || JSON.stringify(error.response?.data)}`;

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(JSON.stringify(error));
        console.error(error);
      }
    }
  };

  const startEditingSelectedRows = () => {
    setEditingSelectedRows(true);
  };

  const afterSavingEditingSelectedRows = ({ refresh }: { refresh: boolean }) => {
    selectedRows.forEach((row) => {
      row.indicators = row.indicators || [];
      row.indicators.push('has_edits');
    });

    // Refresh the selected rows
    if (refresh) refreshSelectedRows();

    setEditingSelectedRows(false);
  };

  const rowCanBeSubmitted = useCallback(
    (row: BillingReportRow): boolean => {
      const notBilledOnCurrentReport =
        !!row.period_end_at && reportEndAt && reportEndAt < new Date(row.period_end_at);

      return (
        (!row.errors || Object.keys(row.errors).length === 0) &&
        !row.manually_held_at &&
        !row.cms_1500_healthie_id &&
        !notBilledOnCurrentReport
      );
    },
    [reportEndAt]
  );
  /**
   * Determines if all selected rows are submittable based on the following criteria:
   * - The row has no errors OR has ignored errors
   * - The row does not have a CMS 1500 Healthie ID
   * @returns {boolean} Whether all selected rows are submittable
   */
  const hasSubmittableRowsSelected = useMemo(() => {
    return selectedRows.every(rowCanBeSubmitted) && hasSelection;
  }, [selectedRows, hasSelection, rowCanBeSubmitted]);

  const submitSelectedRows = async (async = true) => {
    if (!gridRef.current || !gridRef.current.api) return;

    const selectedRows = gridRef.current.api.getSelectedRows();

    // Set submitting flag on selected rows
    selectedRows.forEach((row) => {
      row.isSubmitting = true;
    });
    updateAgGrid([...gridRows]);

    try {
      const result = await reportRepository.submit_selected_rows(selectedRows, async);
      console.log('Submit result:', result);

      setMessages([
        {
          message: 'Submission started. You will be notified when complete.',
          type: 'info'
        }
      ]);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = `Failed to submit service line ${error.response?.data?.error}`;

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(JSON.stringify(error));
        console.error(error);
      }
    }
  };

  /**
   * Common search function for both errors and warnings
   * @param {string} str - The string to search for
   * @param {boolean} onlyUnbilled - Whether to filter for only unbilled items
   * @param {keyof BillingReportRow} type - The type of message to search for
   */
  const searchForTextContains = (
    str: string,
    onlyUnbilled = false,
    type: keyof BillingReportRow
  ) => {
    if (!gridRef.current) return;

    const filters: Record<string, unknown> = {
      cms_1500_healthie_id: {},
      [type]: {
        filterType: 'text',
        type: 'contains',
        filter: str
      }
    };

    if (onlyUnbilled) {
      filters.cms_1500_healthie_id = {
        filterType: 'text',
        type: 'blank',
        filter: true
      };
    }

    // add filter to grid
    gridRef.current.api.setFilterModel(filters);

    setShowErrorSummary(false);

    // scroll grid to top
    gridRef.current.api.ensureIndexVisible(0);

    // scroll window to top of grid
    // compute the offset of the reportRef from the top of the page
    const reportOffset = reportRef.current?.getBoundingClientRect().top || 0;
    const offset = reportOffset + document.documentElement.scrollTop;

    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
  };

  const searchForError = (str: string, onlyUnbilled = false) => {
    searchForTextContains(str, onlyUnbilled, 'errors');
  };

  const searchForWarning = (str: string, onlyUnbilled = false) => {
    searchForTextContains(str, onlyUnbilled, 'warnings');
  };

  const updateUrlWithFilterAndCol = useCallback(
    (gridRef: React.RefObject<AgGridReact<BillingReportRow> | null>) => {
      if (!gridRef.current || !gridRef.current.api) return;
      const columnModel = gridRef.current.api.getColumnState();
      const filterModel = gridRef.current.api.getFilterModel();

      function getInitialIndexOfCol(colId: string): number {
        const columns = colDefs.map((column) => column.field);
        return columns.indexOf(colId);
      }

      const orderedColumns = columnModel
        .filter((col) => !col.hide)
        .map((col) => {
          let partialCol = `${getInitialIndexOfCol(col.colId)}`;
          if (col.sort) {
            partialCol += `:${col.sort}`;
          }
          return partialCol;
        })
        .join(',');

      // put filtermodel into json
      const filterData = JSON.stringify(filterModel);
      const columnData = orderedColumns;

      // Write URL params to current url
      const url = new URL(window.location.href);
      url.search = `?filters=${filterData}&columns=${columnData}`;
      window.history.replaceState({}, '', url.toString());
    },
    [colDefs]
  );

  const onFilterChanged = () => {
    if (!gridRef.current) return;

    updateUrlWithFilterAndCol(gridRef);

    // Update row count
    setRowCount(gridRef.current.api.getDisplayedRowCount());
    setFilterVersion((version) => version + 1);
  };

  const onColumnMoved = () => {
    if (!gridRef.current) return;

    updateUrlWithFilterAndCol(gridRef);
  };

  const openAssociateClaimPanel = async () => {
    if (showAssociateClaimPanel) {
      handleAssociatePanelDismiss();
    } else {
      setShowAssociateClaimPanel(true);
    }
  };

  const handleAssociatePanelDismiss = () => {
    // Close the panel
    setShowAssociateClaimPanel(false);

    // Remove all pending associations from the grid
    if (!gridRef.current) return;
    linkedServiceLines.forEach((row) => {
      const node = gridRef.current?.api.getRowNode(row.billingReportRow.billing_item_id);

      if (node) {
        node.setDataValue('pending_cms_1500_healthie_id', null);
      }
    });

    // Clear context state
    setLinkedServiceLines([]);
  };

  const handlePendingAssociaton = async (linkedServiceLines: LinkedServiceLine[]) => {
    if (!gridRef.current) return;

    // Update the grid with the pending association, so rows are highlighted
    linkedServiceLines.forEach((row) => {
      const node = gridRef.current?.api.getRowNode(row.billingReportRow.service_id);

      if (node) {
        node.setDataValue('pending_cms_1500_healthie_id', row.serviceLine.cms1500);
      }
    });

    // Clear selection
    gridRef.current.api.deselectAll();
  };

  const handleSaveAssociation = async (linkedServiceLines: LinkedServiceLine[]) => {
    if (!gridRef.current) return;

    const data = linkedServiceLines.map((row) => ({
      service_id: row.billingReportRow.service_id,
      cms_1500_cpt_code_id: row.serviceLine.id
    }));

    setLoading({ ...loading, associating: true });
    try {
      const response = await ajax.post({
        url: `${host}/api/billing/billing_report_results/${billing_report_result_id}/link_items/`,
        data: { linked_items: data }
      });

      if (response.status === 200) {
        resetMessages();
        handleAssociatePanelDismiss();
        partialUpdateAgGrid(response.data as RowDataTransaction);
      } else {
        setErrorMessage('Failed to link service lines for unknown reason, Check logs');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverErrorMessage = error.response?.data?.error || error.response?.data;

        if (serverErrorMessage) {
          const errorMessage = `Failed to link service line ${serverErrorMessage}`;
          setErrorMessage(errorMessage);
        } else {
          const errorMessage = `Failed to link service line: ${error.message}`;
          setErrorMessage(errorMessage);
        }
      } else {
        setErrorMessage(JSON.stringify(error));
        console.error(error);
      }
    } finally {
      setLoading({ ...loading, associating: false });
    }
  };

  const handleClaimLoad = (claim: Cms1500) => {
    if (!gridRef.current) return;

    gridRef.current.api.setFilterModel({
      person_id: {
        filterType: 'text',
        type: 'equals',
        filter: claim.patient
      }
    });
  };

  const highlightBillingRow = (billingReportRow: BillingReportRow) => {
    console.log('Highlighting row', billingReportRow);
    if (!gridRef.current) {
      return;
    }

    // Change the current selection to the row
    gridRef.current.api.deselectAll();
    const node = gridRef.current.api.getRowNode(billingReportRow.billing_item_id);
    if (node) {
      gridRef.current.api.setNodesSelected({ nodes: [node], newValue: true });
    }
  };

  const removeLinkedServiceLine = (billingReportRow: BillingReportRow) => {
    if (!gridRef.current) {
      return;
    }

    const billingItemId = billingReportRow.billing_item_id;

    if (!billingItemId) {
      return;
    }

    const node = gridRef.current?.api.getRowNode(billingItemId);
    if (node) {
      node.setDataValue('pending_cms_1500_healthie_id', null);
    }

    setLinkedServiceLines(
      linkedServiceLines.filter(
        (linkedServiceLine) => linkedServiceLine.billingReportRow.billing_item_id !== billingItemId
      )
    );
  };

  const handleMarkRowsToIgnoreError = async () => {
    // If not all rows are ignore error, show the manual ignore error input prompt
    setShowIgnoreErrorInput(true);
  };

  const [linkedServiceLines, setLinkedServiceLines] = useState<LinkedServiceLine[]>([]);

  const handleManualHoldClick = async () => {
    if (!gridRef.current || !gridRef.current.api) return;
    const rows = gridRef.current.api.getSelectedRows();
    if (checkRowsAreAllOnHold()) {
      // If all selected rows are already manual hold, remove the manual hold directly
      await handleClearFlag(BillingItemFlagType.MANUALLY_HELD, false, rows);
    } else {
      // If not all rows are manual hold, show the manual manual hold input prompt
      setShowManualHoldInput(true);
    }
  };

  const hasPendingRefresh = useMemo(() => {
    return selectedRows.some((row) => row.isRefreshing);
  }, [selectedRows]);

  const hasPendingSubmit = useMemo(() => {
    return selectedRows.some((row) => row.isSubmitting);
  }, [selectedRows]);

  return (
    <BillingReportProvider
      healthieUrl={healthie_url}
      reportId={parseInt(billing_report_result_id, 10)}
      setErrorMessage={setErrorMessage}
      setMessages={setMessages}
      reportEndAt={reportEndAt}
      messages={messages}
      apiToken={apiToken}
      gridRef={gridRef}>
      <BillingFlagReasonProvider>
        <AssociateBillingRowsProvider
          value={{
            removeLinkedServiceLine: removeLinkedServiceLine,
            linkedServiceLines: linkedServiceLines,
            setLinkedServiceLines: setLinkedServiceLines,
            highlightBillingRow: highlightBillingRow
          }}>
          <div className="h-full" ref={reportRef}>
            {/* Header links (mimic old Django header actions) */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Button onClick={() => navigate('/billing/reports')} color="white">
                  Back to Reports
                </Button>

                {billingReportResult && billingReportResult.billing_report && (
                  <Button
                    onClick={() =>
                      navigate(`/billing/reports/${billingReportResult.billing_report.id}/results/`)
                    }
                    className="mr-3"
                    color="white">
                    View All Results
                  </Button>
                )}

                {billingReportResult && billingReportResult.billing_report && (
                  <>
                    <Button
                      onClick={handleRegenerateBillingReport}
                      disabled={loading.regenerating}
                      className="mr-3 flex items-center"
                      color="red">
                      <FontAwesomeIcon
                        icon={loading.regenerating ? faSpinner : faRecycle}
                        className={`mr-2 ${loading.regenerating ? 'fa-spin' : ''}`}
                      />
                      {loading.regenerating ? 'Regenerating...' : 'Regenerate'}
                      <Tooltip
                        tooltipContent={
                          <p className="text-sm">
                            Search the date range for all billable items and build the report from
                            scratch. This will restore items that have previously been removed from
                            the report and remove any edits.
                          </p>
                        }>
                        <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
                      </Tooltip>
                    </Button>

                    <Button
                      onClick={handleSubmitAllBillingReport}
                      disabled={loading.submittingAll}
                      className="mr-3 flex items-center"
                      color="yellow">
                      <FontAwesomeIcon
                        icon={loading.submittingAll ? faSpinner : faUpload}
                        className={`mr-2 ${loading.submittingAll ? 'fa-spin' : ''}`}
                      />
                      {loading.submittingAll ? 'Submitting...' : 'Send to Clearinghouse'}
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <AvatarStackForSpace />
                <div className="relative">
                  <Button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={headerMenuOpen}
                    onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
                    className="px-3 py-2"
                    color="white">
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </Button>

                  {headerMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10">
                      <div className="py-2 px-2 bg-white rounded-md">
                        {billingLookupSheetId && (
                          <a
                            href={`https://docs.google.com/spreadsheets/d/${billingLookupSheetId}`}
                            className="dropdown-link block px-3 py-2 text-sm text-gray-700 text-left"
                            target="_blank"
                            rel="noreferrer">
                            <FontAwesomeIcon icon={faTable} className="mr-2" />
                            View Billing Lookup
                          </a>
                        )}

                        {billingReportResult?.billing_report && googleSheetId && (
                          <a
                            href={`https://docs.google.com/spreadsheets/d/${googleSheetId}`}
                            className="dropdown-link block px-3 py-2 text-sm text-gray-700 text-left"
                            target="_blank"
                            rel="noreferrer">
                            <FontAwesomeIcon icon={faTable} className="mr-2" />
                            View Report in Sheets
                          </a>
                        )}

                        <button
                          onClick={handleFlushLookups}
                          disabled={loading.flushingLookups}
                          className="dropdown-link block px-3 py-2 text-sm text-gray-700 text-left w-full disabled:opacity-50">
                          <FontAwesomeIcon
                            icon={loading.flushingLookups ? faSpinner : faToilet}
                            className={`mr-2 ${loading.flushingLookups ? 'fa-spin' : ''}`}
                          />
                          {loading.flushingLookups ? 'Flushing...' : 'Flush Lookups'}
                        </button>

                        {datadogUrl && (
                          <a
                            href={datadogUrl}
                            className="dropdown-link block px-3 py-2 text-sm text-gray-700 text-left"
                            target="_blank"
                            rel="noreferrer">
                            <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                            View Datadog
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Processing banner (if the report result is currently processing) */}
            {billingReportResult && billingReportResult.status === 'processing' && (
              <div className="text-center bg-white rounded shadow p-8 mb-4">
                <div className="inline-block text-yellow-700 bg-yellow-400 p-4 rounded my-4">
                  <span>
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin mr-2" />
                  </span>

                  {`${billingReportResult.status ? billingReportResult.status.replace(/_/g, ' ') + ' ' : ''}job #${billingReportResult.id}`}
                </div>
                <div className="text-gray-600 ">
                  Started at {billingReportResult.processed_started_at}{' '}
                  <span className="text-xs">(processing)</span>
                </div>
              </div>
            )}

            {/* Report Overview */}
            {billingReportResult && (
              <div className="mb-4">
                <ReportOverview billingReportResult={billingReportResult} />
              </div>
            )}

            {/* Non Row Specfic Actions */}
            <div
              className={`${
                showAssociateClaimPanel && 'opacity-50 cursor-not-allowed pointer-events-none'
              } flex mb-2 justify-between`}>
              <nav className="flex space-x-4 items-center" aria-label="Tabs">
                <BillingReportFilters></BillingReportFilters>
                <span className="text-gray-600 mr-1 ">ID: {billing_report_result_id} </span>
              </nav>
              <div className="flex space-x-4 mb-3">
                <Button
                  className="btn btn-blue flex items-center"
                  onClick={openAssociateClaimPanel}
                  disabled={
                    !hasPermission({
                      requiredPermissions: ASSOCIATE_CLAIM_PERMISSIONS,
                      permissions: permissions
                    })
                  }>
                  <FontAwesomeIcon className="mr-2" icon={faLink} />
                  Associate Claim
                </Button>

                <Button
                  className="btn btn-default flex items-center"
                  color="white"
                  onClick={() => {
                    setShowErrorSummary(!showErrorSummary);
                  }}>
                  {showErrorSummary ? (
                    <FontAwesomeIcon icon={faChevronDown} className="mr-1" />
                  ) : (
                    <FontAwesomeIcon icon={faChevronRight} className="mr-1" />
                  )}
                  Alert Summary
                </Button>
              </div>
            </div>
            {/* Row Specfic Actions */}
            <div
              className={`${
                showAssociateClaimPanel && 'opacity-50 cursor-not-allowed pointer-events-none'
              } flex mb-2`}>
              <div className="flex-grow">
                {/* Metrics */}
                <div className="flex mt-2">
                  {loading.initialData ? (
                    <span className="text-gray-400">
                      <FontAwesomeIcon
                        icon={faArrowsRotate}
                        className="fa-spin mr-1"></FontAwesomeIcon>

                      <span>
                        Loading Report
                        {loading.loadingProgress.rows < loading.loadingProgress.totalRows && (
                          <span className="ml-2">
                            {(
                              (loading.loadingProgress.rows / loading.loadingProgress.totalRows) *
                              100
                            ).toFixed(0)}
                            %
                          </span>
                        )}
                      </span>
                    </span>
                  ) : (
                    <>
                      <span className="text-gray-600 mr-1">Rows: </span>
                      <span data-testid="grid-row-count" className="text-gray-800 mr-2">
                        {rowCount}
                      </span>

                      <span className="text-gray-600 mr-1">Filters: </span>
                      <span
                        data-testid="grid-row-filters"
                        className="text-gray-800 mr-2 flex gap-2">
                        {Object.entries(gridRef.current?.api?.getFilterModel() || {}).map(
                          ([field, value]) => {
                            return (
                              <FilterExplanation
                                key={`filter-${field}`}
                                field={field}
                                filterModel={value}
                                gridRef={gridRef}
                              />
                            );
                          }
                        )}
                      </span>
                      <CopyUrlToClipboard />
                    </>
                  )}
                </div>
              </div>
              <div className="flex space-x-4">
                <Button
                  className="flex items-center"
                  color="yellow"
                  disabled={
                    !hasSelection ||
                    loading.holding ||
                    !hasPermission({
                      requiredPermissions: MANUALLY_HOLD_PERMISSIONS,
                      permissions: permissions
                    })
                  }
                  dataTestId="row-action-manual-hold"
                  onClick={handleManualHoldClick}>
                  {loading.holding ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Holding...
                    </>
                  ) : (
                    <></>
                  )}
                  {loading.clearingHold ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Clearing Hold...
                    </>
                  ) : (
                    <></>
                  )}
                  {loading.holding || loading.clearingHold ? (
                    <></>
                  ) : (
                    <>{checkRowsAreAllOnHold() ? 'Clear Hold' : 'Hold'}</>
                  )}
                  <Tooltip
                    tooltipContent={
                      <p className="text-sm">
                        This will toggle the manual hold status of the selected rows, and prevent
                        this item from being billed. It will not effect claim grouping.
                        <br />
                        It will not affect rows that are already on hold, and cannot be used to
                        remove a system hold.
                      </p>
                    }>
                    <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
                  </Tooltip>
                </Button>
                <Button
                  className="btn btn-blue flex items-center"
                  color="blue"
                  disabled={
                    !hasSelection ||
                    loading.ignoringErrors ||
                    !hasPermission({
                      requiredPermissions: IGNORE_ERROR_PERMISSIONS,
                      permissions: permissions
                    })
                  }
                  onClick={handleMarkRowsToIgnoreError}
                  dataTestId="row-action-ignore-error">
                  {loading.ignoringErrors ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Ignoring Errors...
                    </>
                  ) : (
                    <>Ignore Errors</>
                  )}
                  <Tooltip
                    tooltipContent={
                      <p className="text-sm">
                        This will ignore all errors on the selected rows.
                        <br />
                        There is other validation that will occur to ensure the minimal criteria is
                        met. Otherwise, when submitted, this will be passed in with its claim into
                        Healthie, as is.
                      </p>
                    }>
                    <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
                  </Tooltip>
                </Button>
                <Button
                  className="btn btn-yellow flex items-center"
                  color="yellow"
                  disabled={
                    !hasSubmittableRowsSelected ||
                    !hasPermission({
                      requiredPermissions: SUBMIT_BILLING_REPORT_PERMISSIONS,
                      permissions: permissions
                    })
                  }
                  onClick={() => submitSelectedRows()}
                  dataTestId="row-action-submit">
                  {hasPendingSubmit ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>Submit</>
                  )}
                  <Tooltip
                    tooltipContent={
                      <p className="text-sm">
                        This will submit the selected rows to Healthie.
                        <br />
                        This will only submit rows that are valid, and have a claim group ID.
                      </p>
                    }>
                    <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
                  </Tooltip>
                </Button>

                <Button
                  className="btn btn-green flex items-center"
                  color="green"
                  disabled={
                    !hasSelection ||
                    !hasPermission({
                      requiredPermissions: REFRESH_ROW_PERMISSIONS,
                      permissions: permissions
                    })
                  }
                  onClick={refreshSelectedRows}
                  dataTestId="row-action-refresh">
                  {hasPendingRefresh ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon className="mr-2" icon={faRefresh} />
                      Refresh
                    </>
                  )}
                  <Tooltip
                    tooltipContent={
                      <p className="text-sm">
                        This will refresh the selected rows removing any manual changes.
                        <br />
                        <br />
                        It will also regroup, and revalidate all service lines for this person
                      </p>
                    }>
                    <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
                  </Tooltip>
                </Button>
                <Button
                  className="btn btn-red flex items-center"
                  color="red"
                  onClick={removeSelectedRows}
                  disabled={
                    !hasSelection ||
                    !hasPermission({
                      requiredPermissions: DELETE_ROW_PERMISSIONS,
                      permissions: permissions
                    })
                  }
                  dataTestId="row-action-remove">
                  {loading.deleting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon className="mr-2" icon={faTrash} />
                      Delete
                    </>
                  )}
                </Button>
                <Button
                  className="btn btn-green flex items-center"
                  color="green"
                  disabled={
                    !hasSelection ||
                    !hasPermission({
                      requiredPermissions: EDIT_ROW_PERMISSIONS,
                      permissions: permissions
                    })
                  }
                  onClick={startEditingSelectedRows}
                  dataTestId="row-action-start-edit">
                  <FontAwesomeIcon className="mr-2" icon={faPlus} />
                  Edit
                </Button>
              </div>
            </div>

            <div className="flex flex-col">
              {/* Error Message */}
              {messages.map((message, index) => {
                const colors = {
                  error: 'bg-red-100 border-red-400 text-red-700',
                  success: 'bg-green-100 border-green-400 text-green-700',
                  info: 'bg-blue-100 border-blue-400 text-blue-700'
                };

                return (
                  <p
                    key={index}
                    className={`px-4 py-3 rounded relative mb-4 ${colors[message.type]}`}>
                    {message.message}
                    {message.actionText && message.onAction && (
                      <button
                        type="button"
                        className="ml-1 font-bold underline"
                        onClick={message.onAction}>
                        {message.actionText}
                      </button>
                    )}
                    <Button
                      className="absolute top-0 bottom-0 right-0 px-4 py-3"
                      onClick={() => {
                        setMessages(messages.filter((_, i) => i !== index));
                      }}
                      type="button">
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </p>
                );
              })}
            </div>

            {showErrorSummary && (
              <BillingReportErrorSummary
                rows={gridRows}
                searchForError={searchForError}
                searchForWarning={searchForWarning}
              />
            )}
            {showManualHoldInput && (
              <ManualHoldServiceLines
                rows={selectedRows}
                onSaveHold={handleManualHold}
                onClearFlag={handleClearFlag}
                onDismiss={() => setShowManualHoldInput(false)}
              />
            )}
            {showIgnoreErrorInput && (
              <IgnoreErrorServiceLines
                rows={selectedRows}
                onIgnoreError={handleIgnoreError}
                onClearIgnoreErrors={handleClearFlag}
                onDismiss={() => setShowIgnoreErrorInput(false)}
              />
            )}
            {showAssociateClaimPanel && (
              <AssociateServiceLines
                rows={selectedRows}
                onDismiss={() => {
                  handleAssociatePanelDismiss();
                }}
                onClaimLoad={handleClaimLoad}
                onSaveAssociation={handleSaveAssociation}
                onPendingAssociation={handlePendingAssociaton}
              />
            )}
            {editingSelectedRows && (
              <RowEditor
                rows={selectedRows}
                onDismiss={() => setEditingSelectedRows(false)}
                afterSave={afterSavingEditingSelectedRows}
              />
            )}
            <div style={gridStyle} className="h-full">
              <AgGridReact<BillingReportRow>
                ref={gridRef}
                rowData={gridRows}
                getRowId={(params) => params.data.billing_item_id}
                gridOptions={gridOptions}
                columnDefs={colDefs}
                onGridReady={onGridReady}
                rowSelection={{
                  mode: 'multiRow',
                  checkboxes: false,
                  headerCheckbox: false,
                  enableClickSelection: true
                }}
                onRowSelected={onRowSelected}
                onFilterChanged={onFilterChanged}
                onColumnMoved={onColumnMoved}
                onSortChanged={onFilterChanged}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                loading={loading.initialData}
                theme={
                  resolvedTheme === 'dark' ? themeBalham.withPart(colorSchemeDark) : themeBalham
                }></AgGridReact>
            </div>
          </div>
        </AssociateBillingRowsProvider>
      </BillingFlagReasonProvider>
    </BillingReportProvider>
  );
};

export default BillingReportResultApp;
